import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { FormikErrors, FormikValues } from 'formik';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/modules/common/api/index.ts';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import { invalidateQueriesTransactions } from '@/modules/issuer-dashboard/queries/activities.tsx';
import {
  BulkCompleteDrpRequest,
  BulkPaymentSetCompletedStatusRequest,
  CreateDividendEventRequest,
  CreateDividendEventResponse,
  DividendStatus,
  GenerateRedemptionRequestPdfRequest,
  GenerateSubscriptionRequestPdfRequest,
  GetDividendByIdExtendedResponse,
  GetDividendByIdV1SrDividendIdGetParams,
  GetDividendEventListV1SrDividendEventsGetParams,
  GetIssuerDividendEventListResponse,
  PreDividendReportRequest,
  PreDividendReportResponse,
  UpdateDividendEventRequest,
} from 'api';

export const CURRENT_ISSUER_DIVIDEND_EVENT_LIST_QUERY_KEY = 'vestra/issuer-dividend-list';
export const DIVIDEND_BY_ID_QUERY_KEY = 'vestra/issuer-dividend-by-id';
const SHAREHOLDER_EMAIL_SELECTION_STORAGE_KEY_PREFIX = 'sr-dividend-email-selection:';

export interface GenerateIssuerSubscriptionRequestPdfRequest
  extends GenerateSubscriptionRequestPdfRequest {
  registerId?: number;
}

export const useIssuerDividendEventInfiniteListQuery = (
  payload: GetDividendEventListV1SrDividendEventsGetParams & { registerId?: number },
  options: { disabled?: boolean; cacheKey?: string },
) => {
  const [params] = useSearchParams();
  const { page, registerId, ...data } = payload;
  const { disabled = false, cacheKey } = options;
  const normalizedEntityIds = Array.isArray(data.entity_ids)
    ? Array.from(
        new Set(
          data.entity_ids.filter(
            (entityId): entityId is number =>
              typeof entityId === 'number' && !Number.isNaN(entityId),
          ),
        ),
      ).sort((a, b) => a - b)
    : data.entity_ids;
  const normalizedData = {
    ...data,
    ...(Array.isArray(normalizedEntityIds) ? { entity_ids: normalizedEntityIds.join(',') } : {}),
  };

  return useInfiniteQuery<GetIssuerDividendEventListResponse>({
    queryKey: [
      CURRENT_ISSUER_DIVIDEND_EVENT_LIST_QUERY_KEY,
      JSON.stringify(normalizedData),
      registerId || params.get(REGISTER_ID_SEARCH_PARAM_KEY),
      cacheKey,
    ],
    staleTime: 1000 * 60 * 60, // 1 hours
    refetchInterval: 1000 * 60 * 5, //5min
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getDividendEventListV1SrDividendEventsGet(
        {
          ...normalizedData,
          page: pageParam || page,
        },
        registerId ? { headers: { ['xRegister-ID']: registerId } } : undefined,
      );
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};

export const useIssuerDividendEventByIdQuery = (
  payload: GetDividendByIdV1SrDividendIdGetParams,
  disabled = false,
  options?: {
    refetchInterval?: number | false;
  },
) => {
  const [params] = useSearchParams();

  return useQuery<GetDividendByIdExtendedResponse>({
    queryKey: [
      DIVIDEND_BY_ID_QUERY_KEY,
      JSON.stringify(payload),
      params.get(REGISTER_ID_SEARCH_PARAM_KEY),
    ],
    staleTime: 1000 * 60 * 60, // 1 hours
    refetchInterval: 1000 * 60, //1min
    queryFn: async () => {
      const response = await api.v1.getDividendByIdV1SrDividendIdGet(payload);
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
    ...options,
  });
};

export const useDividendArchiveByIdMutation = () => {
  const getRegisterTypeStrings = useRegisterTypeStrings();
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await withErrorHandling(
        api.v1.archiveDividendV1SrArchivingDividendsIdPost({ id }),
        {
          loading: `${getRegisterTypeStrings('dividend', { capitalize: true })} archiving...`,
          success: `${getRegisterTypeStrings('dividend', { capitalize: true })} archived!`,
        },
      );

      return response.data;
    },
    onSuccess: async (_data, id) => {
      if (typeof window !== 'undefined') {
        try {
          Object.keys(window.localStorage)
            .filter(
              key =>
                key.startsWith(SHAREHOLDER_EMAIL_SELECTION_STORAGE_KEY_PREFIX) &&
                key.endsWith(`:${id}`),
            )
            .forEach(key => {
              window.localStorage.removeItem(key);
            });
        } catch (error) {
          console.error('Failed to clear persisted shareholder email selections on archive', error);
        }
      }

      await invalidateQueriesTransactions();
    },
  });
};

export const useIssuerDividendEventListQuery = (
  payload: GetDividendEventListV1SrDividendEventsGetParams,
  disabled = false,
) => {
  const [params] = useSearchParams();

  return useQuery<GetIssuerDividendEventListResponse>({
    queryKey: [
      CURRENT_ISSUER_DIVIDEND_EVENT_LIST_QUERY_KEY,
      JSON.stringify(payload),
      params.get(REGISTER_ID_SEARCH_PARAM_KEY),
    ],
    staleTime: 1000 * 60 * 60, // 1 hours
    refetchInterval: data => {
      const havePending = data?.data?.some(item => item.status === DividendStatus.Pending);
      if (havePending) return false;
      return 60000;
    },
    queryFn: async () => {
      const response = await api.v1.getDividendEventListV1SrDividendEventsGet(payload);
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useIssuerAddDividendEventMutation = ({
  setErrors,
  onSuccess,
}: {
  setErrors?: (errors: FormikErrors<FormikValues>) => void;
  onSuccess?: (data: CreateDividendEventResponse) => void;
}) => {
  const getRegisterTypeStrings = useRegisterTypeStrings();
  return useMutation({
    mutationFn: async (payload: CreateDividendEventRequest & { register_id: number }) => {
      const response = await withErrorHandling(
        api.v1.createDividendEventV1SrDividendEventsPost(payload, {
          headers: { 'xRegister-ID': payload.register_id },
        }),
        {
          loading: `Creating ${getRegisterTypeStrings('dividend')}`,
          success: `${getRegisterTypeStrings('dividend', { capitalize: true })} created`,
          notificationDuration: 10000,
          setErrors,
        },
      );

      return response.data;
    },
    onSuccess: async data => {
      await invalidateQueriesTransactions();
      onSuccess && onSuccess(data);
    },
  });
};

export const useIssuerGeneratePreviewDividendMutation = ({
  setErrors,
  onSuccess,
}: {
  setErrors: (errors: FormikErrors<FormikValues>) => void;
  onSuccess?: (previewUrl?: string) => void;
}) => {
  const getRegisterTypeStrings = useRegisterTypeStrings();
  return useMutation({
    mutationFn: async (payload: PreDividendReportRequest & { register_id: number }) => {
      const response = await withErrorHandling(
        api.v1.generatePreDividendReportV1SrPreDividendReportPost(payload, {
          headers: { 'xRegister-ID': payload.register_id },
        }),
        {
          loading: `Generating preview of this ${getRegisterTypeStrings('dividend')}...`,
          success: `Preview of ${getRegisterTypeStrings('dividend', {
            capitalize: false,
          })} generated, please review it and confirm to proceed distribution.`,
          notificationDuration: 10000,
          setErrors,
        },
      );

      return response.data;
    },
    onSuccess: (data: PreDividendReportResponse) => {
      onSuccess && onSuccess(data?.report?.file_storage_item_url);
    },
  });
};

export const useIssuerUpdateStatusDividendEventMutation = ({
  onSuccess,
}: {
  onSuccess?: () => void | Promise<void>;
} = {}) => {
  const getRegisterTypeStrings = useRegisterTypeStrings();
  return useMutation({
    mutationFn: async (payload: UpdateDividendEventRequest & { register_id: number }) => {
      const response = await withErrorHandling(
        api.v1.updateDividendStatusV1SrDividendStatusPut(payload, {
          headers: { 'xRegister-ID': payload.register_id },
        }),
        {
          loading: `Finalising ${getRegisterTypeStrings('dividend')}`,
          success: `${getRegisterTypeStrings('dividend', {
            capitalize: true,
          })} finalised, investors will be notified by email`,
          notificationDuration: 10000,
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      onSuccess && (await onSuccess());
      await invalidateQueriesTransactions();
    },
  });
};

export const useGenerateInvestorDistributionReportsMutation = ({
  onSuccess,
}: {
  onSuccess?: () => void | Promise<void>;
} = {}) => {
  return useMutation({
    mutationFn: async ({ id, register_id }: { id: number; register_id: number }) => {
      try {
        const response = await withErrorHandling(
          api.v1.generateInvestorDistributionReportsV1SrDividendIdInvestorDistributionReportsGeneratePost(
            { id },
            { headers: { 'xRegister-ID': register_id } },
          ),
          {
            loading: 'Searching and removing old reports...',
            success: '',
          },
        );

        const deletedReportsCount = (response.data as { deleted_reports_count?: number })
          ?.deleted_reports_count;
        const count = typeof deletedReportsCount === 'number' ? deletedReportsCount : 0;
        const successMessage =
          count === 0
            ? 'No old reports found. Started generating new reports.'
            : `Deleted ${count} old ${
                count === 1 ? 'report' : 'reports'
              }. Started generating new reports.`;
        toast.success(successMessage);

        return { isConflict: false as const };
      } catch (error: unknown) {
        if ((error as { response?: { status?: number } }).response?.status === 409) {
          return { isConflict: true as const };
        }
        throw error;
      }
    },
    onSuccess: async result => {
      if (!result.isConflict) {
        onSuccess && (await onSuccess());
      }
    },
  });
};

export const useGenerateDistributionReportsMutation = ({
  onSuccess,
}: {
  onSuccess?: () => void | Promise<void>;
} = {}) => {
  return useMutation({
    mutationFn: async ({ id, register_id }: { id: number; register_id: number }) => {
      try {
        const response = await withErrorHandling(
          api.v1.generateDistributionReportsV1SrDividendIdDistributionReportsGeneratePost(
            { id },
            { headers: { 'xRegister-ID': register_id } },
          ),
          {
            loading: 'Searching and removing old finalised reports...',
            success: '',
          },
        );

        const deletedReportsCount = (response.data as { deleted_reports_count?: number })
          ?.deleted_reports_count;
        const count = typeof deletedReportsCount === 'number' ? deletedReportsCount : 0;
        const successMessage =
          count === 0
            ? 'No old finalised reports found. Started generating new reports.'
            : `Deleted ${count} old finalised ${
                count === 1 ? 'report' : 'reports'
              }. Started generating new reports.`;
        toast.success(successMessage);

        return { isConflict: false as const };
      } catch (error: unknown) {
        if ((error as { response?: { status?: number } }).response?.status === 409) {
          return { isConflict: true as const };
        }
        throw error;
      }
    },
    onSuccess: async result => {
      if (!result.isConflict) {
        onSuccess && (await onSuccess());
      }
    },
  });
};

export const useIssuerGenerateABAFileDividendMutation = ({
  onSuccess,
}: {
  onSuccess?: (base64file?: string) => void;
}) => {
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await withErrorHandling(
        api.v1.bulkPaymentFileV1SrDividendIdBulkPaymentFileGet({ id }),
        {
          loading: `Generating ABA file`,
          success: `Generated ABA file successful`,
          errorDuration: 10000,
        },
      );

      return response.data;
    },
    onSuccess: data => {
      onSuccess && onSuccess(data.payment_file);
    },
  });
};

export const useDividendPaymentsBulkCompleteMutation = ({
  onSuccess,
}: {
  onSuccess?: () => Promise<void> | void;
} = {}) => {
  return useMutation({
    mutationFn: async (payload: BulkPaymentSetCompletedStatusRequest) => {
      const response = await withErrorHandling(
        api.v1.paymentBulkSetCompletedV1SrPaymentsStatusBulkCompletedPatch(payload),
        {
          loading: `Updating payments...`,
          success: `Updated payments successful`,
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      onSuccess && (await onSuccess());
      await invalidateQueriesTransactions({
        updateDividend: false,
      });
    },
  });
};

export const useTransactionAwaitingPaymentBulkCompleteMutation = ({
  onSuccess,
  setErrors,
}: {
  onSuccess?: () => void | Promise<void>;
  setErrors?: (errors: FormikErrors<FormikValues>, payload: BulkCompleteDrpRequest) => void;
} = {}) => {
  return useMutation({
    mutationFn: async (payload: BulkCompleteDrpRequest) => {
      const response = await withErrorHandling(
        api.v1.transactionBulkSetAwaitPaymentV1SrTransactionsStatusBulkAwaitPaymentPatch(payload),
        {
          loading: 'Updating transactions...',
          success: 'Transactions updated',
          setErrors: setErrors ? errors => setErrors(errors, payload) : undefined,
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      onSuccess && (await onSuccess());
      await invalidateQueriesTransactions({ updateDividend: false });
    },
  });
};

export const useIssuerGenerateRedemptionPdfFormMutation = ({
  setErrors,
  onSuccess,
}: {
  setErrors?: (errors: FormikErrors<FormikValues>) => void;
  onSuccess?: (file?: Blob) => void;
}) => {
  return useMutation({
    mutationFn: async (payload: GenerateRedemptionRequestPdfRequest & { entityId: number }) => {
      const response = await withErrorHandling(
        api.v1.createRedemptionRequestReportV1SrRedemptionRequestPost(payload, {
          headers: { ['xEntity-ID']: payload.entityId },
          format: 'blob',
        }),
        {
          loading: `Generating Redemption PDF form...`,
          success: `Redemption PDF form generated`,
          setErrors,
        },
      );

      const data = response.data;

      if (!(data instanceof Blob)) {
        return data as Blob;
      }

      const contentType = String(response.headers?.['content-type'] || data.type);
      if (!contentType?.toLowerCase()?.includes('application/json')) {
        return data;
      }

      const text = await data.text();
      const parsed = JSON.parse(text) as {
        data?: string;
        mime_type?: string;
      };

      if (!parsed.data) {
        return data;
      }

      const bytes = Uint8Array.from(atob(parsed.data), char => char.charCodeAt(0));
      return new Blob([bytes], { type: parsed.mime_type || 'application/pdf' });
    },
    onSuccess: (file: Blob) => {
      onSuccess && onSuccess(file);
    },
  });
};

export const useIssuerGenerateSubscriptionPdfFormMutation = ({
  setErrors,
  onSuccess,
}: {
  setErrors?: (errors: FormikErrors<FormikValues>) => void;
  onSuccess?: (file?: Blob) => void;
}) => {
  return useMutation({
    mutationFn: async ({ registerId, ...payload }: GenerateIssuerSubscriptionRequestPdfRequest) => {
      const response = await withErrorHandling(
        api.v1.createSubscriptionRequestReportV1SrSubscriptionRequestPost(payload, {
          headers: registerId ? { ['xRegister-ID']: registerId } : undefined,
          format: 'blob',
        }),
        {
          loading: `Generating Subscription Document...`,
          success: `Subscription Document generated`,
          setErrors,
        },
      );

      const data = response.data;

      if (!(data instanceof Blob)) {
        return data as Blob;
      }

      const contentType = String(response.headers?.['content-type'] || data.type);
      if (!contentType?.toLowerCase()?.includes('application/json')) {
        return data;
      }

      const text = await data.text();
      const parsed = JSON.parse(text) as {
        data?: string;
        mime_type?: string;
      };

      if (!parsed.data) {
        return data;
      }

      const bytes = Uint8Array.from(atob(parsed.data), char => char.charCodeAt(0));
      return new Blob([bytes], { type: parsed.mime_type || 'application/pdf' });
    },
    onSuccess: (file: Blob) => {
      onSuccess && onSuccess(file);
    },
  });
};
